import { useId } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import type { Schemas } from "@tern/api-client";

export type DatabaseConnectionValue = Omit<Schemas["DatabaseInput"], "port"> & { port: string };
export const emptyDatabaseInput = (): DatabaseConnectionValue => ({
  host: "", port: "5432", user: "", password: "", database: "", ssl_mode: "verify-full",
});
export const databaseInputComplete = (value: DatabaseConnectionValue) =>
  Boolean(value.host.trim() && value.user.trim() && value.password && value.database.trim()
    && /^\d+$/.test(value.port) && Number(value.port) >= 1 && Number(value.port) <= 65535);
export const databaseInputPayload = (value: DatabaseConnectionValue): Schemas["DatabaseInput"] => ({
  ...value, host: value.host.trim(), port: Number(value.port),
});

export function DatabaseConnectionFields({ value, onChange, disabled }: {
  value: DatabaseConnectionValue;
  onChange: (value: DatabaseConnectionValue) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const change = (key: keyof DatabaseConnectionValue, text: string) => onChange({ ...value, [key]: text });
  const labelClass = "text-right text-[13px] font-medium text-muted";
  const inputClass = "input h-10 min-w-0 w-full";

  return <div className="grid grid-cols-[76px_minmax(0,1fr)] items-center gap-x-4 gap-y-4">
    <label className={labelClass} htmlFor={`${id}-host`}>Host</label>
    <div className="grid grid-cols-[minmax(0,1fr)_auto_92px] items-center gap-3">
      <input id={`${id}-host`} className={inputClass} name="host" type="text" autoComplete="off"
        autoCapitalize="none" spellCheck={false} placeholder="db.company.com" value={value.host}
        onChange={(event) => change("host", event.target.value)} required disabled={disabled} />
      <label className={labelClass} htmlFor={`${id}-port`}>Port</label>
      <input id={`${id}-port`} className={`${inputClass} tabular-nums`} name="port" type="text" inputMode="numeric"
        pattern="[0-9]+" maxLength={5} autoComplete="off" placeholder="5432" value={value.port}
        onChange={(event) => change("port", event.target.value)} required disabled={disabled}
        aria-invalid={value.port !== "" && (!/^\d+$/.test(value.port) || Number(value.port) < 1 || Number(value.port) > 65535) || undefined} />
    </div>
    {([
      { key: "user", label: "User", placeholder: "tern_owner", type: "text" },
      { key: "password", label: "Password", placeholder: "", type: "password" },
      { key: "database", label: "Database", placeholder: "tern_company", type: "text" },
    ] as const).map(({ key, label, placeholder, type }) => <div key={key} className="contents">
      <label className={labelClass} htmlFor={`${id}-${key}`}>{label}</label>
      <input id={`${id}-${key}`} className={inputClass} name={key} type={type}
        autoComplete={key === "password" ? "new-password" : "off"} autoCapitalize="none" spellCheck={false}
        placeholder={placeholder} value={value[key]} onChange={(event) => change(key, event.target.value)}
        required disabled={disabled} />
    </div>)}
    <details className="group col-start-2 text-[12px]">
      <summary className="flex w-fit cursor-pointer list-none items-center gap-1.5 text-muted hover:text-text [&::-webkit-details-marker]:hidden">
        {t("storage.otherOptions")}<ChevronDown size={14} className="transition-transform group-open:rotate-180" />
      </summary>
      <div className="mt-3 flex flex-col gap-2">
        <label className="font-medium text-muted" htmlFor={`${id}-ssl`}>{t("storage.sslLabel")}</label>
        <select id={`${id}-ssl`} className={inputClass} value={value.ssl_mode ?? "verify-full"}
          disabled={disabled} onChange={(event) => change("ssl_mode", event.target.value)}>
          <option value="verify-full">{t("storage.sslVerify")}</option>
          <option value="require">{t("storage.sslRequire")}</option>
          <option value="disable">{t("storage.sslDisable")}</option>
        </select>
        {value.ssl_mode === "disable" && <p className="text-muted">{t("storage.sslDisabledHint")}</p>}
      </div>
    </details>
  </div>;
}
